import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { normalizeActionAIModelSelections } from '../../Utils/actionAIModelUtils';
import { readActionFunnelStage } from '../../Utils/actionFunnel';
import { normalizeActionSettingsPayload } from '../../Utils/actionSettingsUtils';

const ActionScopeContext = createContext('event');
const ActionSettingsPayloadContext = createContext(null);
const ActionSettingsCommandContext = createContext(null);
const ActionCaseIdContext = createContext(null);
const ActionEditorControlsContext = createContext(null);
const ActionEditorHeaderContext = createContext(null);
const ActionEditorGeneralSettingsContext = createContext(null);
const ActionTypeContext = createContext('');
const ActionAIModelContext = createContext(null);
const ActionWidgetConfirmationContext = createContext(null);
const ActionFunnelContext = createContext(null);

export const ActionScopeProvider = ({
  scope = 'event',
  caseid = null,
  setting = null,
  sendActionCommand = null,
  registerEditorControls = null,
  editorHeader = null,
  editorGeneralSettings = null,
  actionType = '',
  isPluginAction = false,
  children
}) => {
  const normalizedSetting = useMemo(() => setting ? normalizeActionSettingsPayload(setting) : null, [setting]);
  const [funnelStage, setFunnelStage] = useState(() => readActionFunnelStage(normalizedSetting?.customData));
  const editorIdentity = `${caseid}:${setting?.id}:${actionType}`;
  const previousIdentity = useRef(editorIdentity);
  const [actionAIModels, setActionAIModels] = useState(() => (
    normalizeActionAIModelSelections(normalizedSetting?.customData)
  ));
  const [requireConfirmedWidgets, setRequireConfirmedWidgets] = useState(() => (
    normalizedSetting?.customData?.requireConfirmedWidgets === true
  ));

  useEffect(() => {
    if (previousIdentity.current === editorIdentity) return;
    previousIdentity.current = editorIdentity;
    // Later versions of this action hydrate through useActionSettings only.
    // The provider resets local state solely when opening a different editor.
    setActionAIModels(normalizeActionAIModelSelections(normalizedSetting?.customData));
    setRequireConfirmedWidgets(normalizedSetting?.customData?.requireConfirmedWidgets === true);
    setFunnelStage(readActionFunnelStage(normalizedSetting?.customData));
  }, [editorIdentity, normalizedSetting]);

  const setActionAIModel = useCallback((modelType, modelId) => {
    const fieldName = {
      text: 'aiTextModelId',
      vision: 'aiVisionModelId',
      audio: 'aiAudioModelId',
    }[modelType];
    if (!fieldName) {
      return;
    }
    setActionAIModels((current) => ({
      ...current,
      [fieldName]: modelId ? String(modelId) : '',
    }));
  }, []);

  const actionAIModelContextValue = useMemo(() => ({
    actionAIModels,
    setActionAIModels,
    setActionAIModel,
    isPluginAction,
  }), [actionAIModels, isPluginAction, setActionAIModel]);
  const widgetConfirmation = useMemo(() => ({
    requireConfirmedWidgets,
    setRequireConfirmedWidgets,
  }), [requireConfirmedWidgets]);
  const funnelSettings = useMemo(() => ({ funnelStage, setFunnelStage }), [funnelStage, setFunnelStage]);

  return (
    <ActionEditorGeneralSettingsContext.Provider value={editorGeneralSettings}>
      <ActionEditorHeaderContext.Provider value={editorHeader}>
        <ActionEditorControlsContext.Provider value={registerEditorControls}>
          <ActionSettingsCommandContext.Provider value={sendActionCommand}>
            <ActionSettingsPayloadContext.Provider value={normalizedSetting}>
              <ActionCaseIdContext.Provider value={caseid}>
                <ActionTypeContext.Provider value={actionType}>
                  <ActionAIModelContext.Provider value={actionAIModelContextValue}>
                    <ActionScopeContext.Provider value={scope || 'event'}>
                      <ActionWidgetConfirmationContext.Provider value={widgetConfirmation}>
                        <ActionFunnelContext.Provider value={funnelSettings}>{children}</ActionFunnelContext.Provider>
                      </ActionWidgetConfirmationContext.Provider>
                    </ActionScopeContext.Provider>
                  </ActionAIModelContext.Provider>
                </ActionTypeContext.Provider>
              </ActionCaseIdContext.Provider>
            </ActionSettingsPayloadContext.Provider>
          </ActionSettingsCommandContext.Provider>
        </ActionEditorControlsContext.Provider>
      </ActionEditorHeaderContext.Provider>
    </ActionEditorGeneralSettingsContext.Provider>
  );
};

export const useActionScope = () => useContext(ActionScopeContext) || 'event';
export const useActionSettingsPayload = () => useContext(ActionSettingsPayloadContext);
export const useActionSettingsCommand = () => useContext(ActionSettingsCommandContext);
export const useActionCaseId = () => useContext(ActionCaseIdContext);
export const useActionEditorControls = () => useContext(ActionEditorControlsContext);
export const useActionEditorHeader = () => useContext(ActionEditorHeaderContext);
export const useActionEditorGeneralSettings = () => useContext(ActionEditorGeneralSettingsContext);
export const useActionType = () => useContext(ActionTypeContext) || '';
export const useActionWidgetConfirmation = () => useContext(ActionWidgetConfirmationContext);
export const useActionFunnel = () => useContext(ActionFunnelContext);
export const useActionAIModels = () => useContext(ActionAIModelContext) || {
  actionAIModels: normalizeActionAIModelSelections(),
  setActionAIModels: () => {},
  setActionAIModel: () => {},
  isPluginAction: false,
};

export default ActionScopeContext;
